import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Truck, ArrowRight, CheckCircle, Warning, HandPointing,
} from "@phosphor-icons/react";

/**
 * Public "Request Dispatch Services" page — for CARRIERS looking to be
 * dispatched. Roll Fast Solutions is a dispatch service (not a licensed
 * freight broker), so this is the correct front door.
 *
 * POSTs to /api/public/dispatch-request. No auth required.
 */

const EQUIPMENT_OPTIONS = [
  "Dry Van", "Reefer", "Flatbed", "Step Deck",
  "Box Truck", "Power Only", "Hot Shot", "Tanker",
];

const FLEET_SIZE_OPTIONS = [
  "1 truck (Owner-Operator)", "2-5 trucks", "6-10 trucks",
  "11-25 trucks", "26-50 trucks", "50+ trucks",
];

const DISPATCH_NEEDS_OPTIONS = [
  "Full dispatch service", "Backup / overflow dispatch",
  "Weekend / after-hours coverage", "Lane-specific dispatch",
  "Just exploring — need more info",
];

const BACKEND = process.env.REACT_APP_BACKEND_URL;


export default function RequestDispatchServices() {
  const [form, setForm] = useState({
    company: "", contact_name: "", contact_email: "", contact_phone: "",
    mc_number: "", dot_number: "", equipment: "", fleet_size: "",
    preferred_lanes: "", dispatch_needs: "", notes: "",
    honeypot: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${BACKEND}/api/public/dispatch-request`, form);
      setDone(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (done?.ok) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4"
        style={{ background: "#050505" }}>
        <div className="max-w-lg w-full text-center space-y-6 p-8 rounded-lg"
          style={{ background: "#0A0A0A", border: "1px solid #262626" }}
          data-testid="dispatch-request-success">
          <CheckCircle size={56} weight="fill" color="#22C55E" className="mx-auto" />
          <div>
            <h1 className="text-3xl font-black text-white mb-2">Request received</h1>
            <p className="text-neutral-400">
              {done.message || "Thanks — a dispatch specialist will reach out within one business day to discuss your operation and set up a consultation."}
            </p>
          </div>
          {done.lead_id && (
            <div className="rounded p-3 font-mono text-xs text-neutral-500"
              style={{ background: "#0F0F0F", border: "1px solid #171717" }}>
              Reference: {done.lead_id}
            </div>
          )}
          <div className="flex gap-3 justify-center">
            <Link to="/carrier/signup"
              className="px-4 py-2 rounded text-sm font-bold uppercase tracking-widest"
              style={{ background: "#0F0F0F", color: "#A3A3A3", border: "1px solid #262626" }}>
              Complete carrier signup
            </Link>
            <button onClick={() => { setDone(null); setForm({
              company: "", contact_name: "", contact_email: "", contact_phone: "",
              mc_number: "", dot_number: "", equipment: "", fleet_size: "",
              preferred_lanes: "", dispatch_needs: "", notes: "", honeypot: "" }); }}
              className="px-4 py-2 rounded text-sm font-bold uppercase tracking-widest"
              style={{ background: "#DC2626", color: "white" }}
              data-testid="dispatch-request-another">
              Submit another request
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-10 px-4" style={{ background: "#050505" }}>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="dispatch-request-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <img src="/logo.png?v=rf2026-newlogo-0804" alt="Roll Fast" className="w-12 h-12 rounded object-cover"
            style={{ border: "1px solid #262626" }} />
          <div>
            <div className="text-[11px] uppercase tracking-widest font-bold text-red-500">
              Roll Fast Solutions · Nationwide Truck Dispatch
            </div>
            <h1 className="text-3xl font-black text-white">Request Dispatch Services</h1>
            <p className="text-neutral-400 text-sm">
              We book profitable loads for owner-operators and fleets across the U.S. Tell us about your operation and a dispatcher will follow up within one business day.
            </p>
          </div>
        </div>

        {/* Trust bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <TrustCard Icon={HandPointing} title="Your own dispatcher"
            body="A real person who learns your lanes, your truck, and how you like to run." />
          <TrustCard Icon={Truck} title="Loads while you drive"
            body="We hunt loads, negotiate rates, and handle paperwork so you focus on driving." />
          <TrustCard Icon={CheckCircle} title="Onboarded in a day"
            body="Send your MC and docs — you're set up and booking loads within 24 hours." />
        </div>

        {/* Form */}
        <form onSubmit={submit} className="space-y-5 rounded-lg p-6"
          style={{ background: "#0A0A0A", border: "1px solid #262626" }}>

          {/* Honeypot */}
          <input type="text" name="fax_number" value={form.honeypot}
            onChange={e => setField("honeypot", e.target.value)}
            tabIndex={-1} autoComplete="off"
            style={{ position: "absolute", left: "-9999px", opacity: 0 }}
            aria-hidden="true" />

          <Section title="Carrier Details">
            <Row>
              <Input label="Company (legal name)" required
                value={form.company} onChange={v => setField("company", v)}
                testid="dispatch-company" />
              <Input label="Contact name" required
                value={form.contact_name} onChange={v => setField("contact_name", v)}
                testid="dispatch-contact-name" />
            </Row>
            <Row>
              <Input label="Email" type="email" required
                value={form.contact_email} onChange={v => setField("contact_email", v)}
                testid="dispatch-email" />
              <Input label="Phone" required
                value={form.contact_phone} onChange={v => setField("contact_phone", v)}
                placeholder="+1 (555) 123-4567"
                testid="dispatch-phone" />
            </Row>
            <Row>
              <Input label="MC number" required placeholder="e.g., 900001"
                value={form.mc_number} onChange={v => setField("mc_number", v)}
                testid="dispatch-mc" />
              <Input label="DOT number" placeholder="e.g., 3123456"
                value={form.dot_number} onChange={v => setField("dot_number", v)}
                testid="dispatch-dot" />
            </Row>
          </Section>

          <Section title="Fleet & Equipment">
            <Row>
              <Select label="Primary equipment" required options={EQUIPMENT_OPTIONS}
                value={form.equipment} onChange={v => setField("equipment", v)}
                testid="dispatch-equipment" />
              <Select label="Fleet size" required options={FLEET_SIZE_OPTIONS}
                value={form.fleet_size} onChange={v => setField("fleet_size", v)}
                testid="dispatch-fleet" />
            </Row>
            <Input label="Preferred lanes"
              placeholder="e.g., TX ↔ CA, or Regional Midwest"
              value={form.preferred_lanes} onChange={v => setField("preferred_lanes", v)}
              testid="dispatch-lanes" />
          </Section>

          <Section title="Dispatch Needs">
            <Select label="What kind of dispatch coverage do you need?"
              required options={DISPATCH_NEEDS_OPTIONS}
              value={form.dispatch_needs} onChange={v => setField("dispatch_needs", v)}
              testid="dispatch-needs" />
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-1">
                Additional notes
              </div>
              <textarea rows={4} value={form.notes}
                onChange={e => setField("notes", e.target.value)}
                placeholder="Anything else we should know? Home base, running lanes, avoid-list, minimum RPM…"
                className="w-full rounded bg-neutral-950 border border-neutral-800 p-3 text-sm text-neutral-200 outline-none focus:border-red-800"
                data-testid="dispatch-notes" />
            </div>
          </Section>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2 border-t"
            style={{ borderColor: "#171717" }}>
            <div className="flex items-center gap-2 text-xs text-neutral-500">
              <Warning size={12} weight="bold" />
              Roll Fast Solutions is a dispatch service — we work directly with motor carriers.
            </div>
            <button type="submit" disabled={busy}
              data-testid="dispatch-submit-btn"
              className="flex items-center gap-2 px-6 py-3 rounded font-bold text-sm uppercase tracking-widest disabled:opacity-40"
              style={{ background: "#DC2626", color: "white" }}>
              <Truck size={14} weight="bold" />
              {busy ? "Submitting…" : "Request Dispatch Services"}
              <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        </form>

        {/* Footer nav */}
        <div className="flex flex-wrap items-center justify-between gap-4 text-xs text-neutral-500">
          <Link to="/" className="hover:text-red-500">← Back to home</Link>
          <div className="flex items-center gap-4">
            <Link to="/carrier/signup" className="flex items-center gap-1 hover:text-red-500">
              <Truck size={12} weight="bold" /> Already a carrier? Complete signup
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}


function TrustCard({ Icon, title, body }) {
  return (
    <div className="rounded p-3 space-y-1"
      style={{ background: "#0A0A0A", border: "1px solid #262626" }}>
      <div className="flex items-center gap-2">
        <Icon size={16} weight="fill" color="#DC2626" />
        <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-100">
          {title}
        </div>
      </div>
      <div className="text-xs text-neutral-400">{body}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 pb-1 border-b"
        style={{ borderColor: "#171717" }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Input({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <label className="block text-sm">
      <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-1">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </div>
      <input type={type} value={value} required={required} placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className="w-full rounded bg-neutral-950 border border-neutral-800 px-3 py-2 text-sm text-neutral-200 outline-none focus:border-red-800" />
    </label>
  );
}

function Select({ label, options, value, onChange, testid, required }) {
  return (
    <label className="block text-sm">
      <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500 mb-1">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </div>
      <select value={value} required={required} onChange={e => onChange(e.target.value)}
        data-testid={testid}
        className="w-full rounded bg-neutral-950 border border-neutral-800 px-3 py-2 text-sm text-neutral-200 outline-none focus:border-red-800">
        <option value="">— Select —</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}
